import React, { useEffect, useRef, useState } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Divider,
  Stack,
  Card,
  CardContent,
  Avatar,
} from '@mui/material';
import { Edit, Print } from '@mui/icons-material';
import AuthService from '../Auth/AuthService';
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const initialFormData = {
  teacherName: '',
  fatherName: '',
  address: '',
  designation: '',
  subject: '',
  joiningDate: '',
  salary: '',
  workingHours: '8:00 AM to 3:00 PM, Monday to Friday',
  probationPeriod: '6 months',
  institutionName: 'JMK International School',
  affiliationNo: '4512',
  schoolAddress: 'Plot No. 100, Nagpur-12 MH',
  issueDate: new Date().toISOString().split('T')[0],
  principalName: 'Dr. Johnluke',
};

export default function OfferLetterGenerator({ onSuccess, user }) {
  const [formData, setFormData] = useState(initialFormData);
  const masterId = user?.mas_id;
  const hasFetched = useRef(false);
  const [school, setSchool] = useState({});
  const DOCURL = AuthService.DOC_URL;

  const [show, setShow] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    loadSchool();
  }, []);

  const loadSchool = async () => {
    try {
      if (user?.mas_id !== undefined) {
        const url = AuthService.pathUrl("GET", "/schools/email");
        const schoolsData = await AuthService.getAuth(url);
        setSchool(schoolsData);
      }
    } catch (error) {
      console.error("Error fetching schools:", error);
    }
  }

  const handlePost = async () => {
    // setShow(false);
    try {
      const url = AuthService.pathUrl("POST", "/affer-letter");
      if (user?.mas_id !== undefined)
        await AuthService.postAuth(url, formData);
      else
        await AuthService.restPost(url, formData);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Error fetching offer-letter-preview:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // const handlePrint = async () => {
  //   setShow(false);
  //   handlePost();
  //   setTimeout(() => {
  //     window.print();
  //     setShow(true);
  //   }, 1000)
  // };

  const handlePrint = () => {
    handlePost();
    const printContent = document.getElementById('offer-letter');
    const originalContents = document.body.innerHTML;
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  const downloadCertificatePDF = async (id, filename) => {
    // setShow(false);
    const element = document.getElementById(id);
    // handlePost();
    const images = element?.querySelectorAll('img');
    const originalSrcs = [];
    if (images !== undefined) {
      for (const img of images) {
        originalSrcs.push(img.src);
        const dataUrl = await fetch(img.src)
          .then(res => {
            if (!res.ok) throw new Error(`Failed to fetch ${img.src}`);
            return res.blob();
          })
          .then(blob => new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          }));
        img.src = dataUrl;
      }
    }

    const canvas = await html2canvas(element, { scale: 2 });
    const img = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'px', 'a4');
    const w = 210;
    const h = (canvas.height * w) / canvas.width;
    pdf.addImage(img, 'PNG', 25, 25, w, h);
    pdf.save(filename);

    if (images !== undefined) {
      images.forEach((img, idx) => {
        img.src = originalSrcs[idx];
      });
    }
    setTimeout(() => {
      setShow(true);
    }, 500)
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Box maxWidth="lg" sx={{ p: 4, mx: 'auto' }}>
      {show && (
        <Typography variant="h4" gutterBottom align="center" color="primary">
          Teacher Appointment Letter Generator
        </Typography>
      )}

      {/* Flexbox container for two columns, centered */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',     // allows stacking on mobile
          justifyContent: 'center', // center the columns as a group
          gap: 4,               // spacing between columns
        }}
      >
        {/* Left Column – Form */}
        {show && (
          <Box sx={{ flex: '1 1 350px', minWidth: '200px' }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Teacher Details
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Teacher Name"
                  name="teacherName"
                  value={formData.teacherName}
                  onChange={handleChange}
                  fullWidth
                  required
                />
                <TextField
                  label="Father's/Husband's Name"
                  name="fatherName"
                  value={formData.fatherName}
                  onChange={handleChange}
                  fullWidth
                  required
                />
                <TextField
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  fullWidth
                  required
                  // multiline
                  rows={2}
                />
                <TextField
                  label="Designation"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  fullWidth
                  required
                />
                <TextField
                  label="Subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  fullWidth
                  required
                />
                <TextField
                  label="Joining Date"
                  name="joiningDate"
                  type="date"
                  value={formData.joiningDate}
                  onChange={handleChange}
                  fullWidth
                  required
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  label="Salary"
                  name="salary"
                  value={formData.salary}
                  onChange={handleChange}
                  fullWidth
                  required
                />
                <TextField
                  label="Working Hours"
                  name="workingHours"
                  value={formData.workingHours}
                  onChange={handleChange}
                  fullWidth
                  required
                />
                <TextField
                  label="Probation Period"
                  name="probationPeriod"
                  value={formData.probationPeriod}
                  onChange={handleChange}
                  fullWidth
                  required
                />
                {!masterId && (<>
                  <TextField
                    label="Institution Name"
                    name="institutionName"
                    value={formData.institutionName}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Affiliation No"
                    name="affiliationNo"
                    value={formData.affiliationNo}
                    onChange={handleChange}
                    fullWidth
                    required
                  />

                  <TextField
                    label="School Address"
                    name="schoolAddress"
                    value={formData.schoolAddress}
                    onChange={handleChange}
                    fullWidth
                    required
                    // multiline
                    rows={2}
                  />
                </>)}

                <TextField
                  label="Issue Date"
                  name="issueDate"
                  type="date"
                  value={formData.issueDate}
                  onChange={handleChange}
                  fullWidth
                  required
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                {!masterId && (
                  <TextField
                    label="Principal's Name"
                    name="principalName"
                    value={formData.principalName}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                )}
              </Stack>
            </Paper>
          </Box>
        )}

        {/* Right Column – Preview */}
        <Box sx={{ flex: '1 1 550px', minWidth: '400px' }}>
          <Card sx={{ p: 3, minHeight: '70vh' }} id="offer-letter">
            <CardContent>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Box display="flex" justifyContent="center" alignItems="center" flexWrap="wrap" gap={2}>
                  <Avatar
                    src={school?.logoUrl ? DOCURL + school.logoUrl : previewUrl}
                    alt="School Logo"
                    sx={{ width: 80, height: 80, borderRadius: 1 }}
                  />
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {school?.schoolName || formData.institutionName}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Recognised by Govt. of India | Affiliation No: {school?.diseCode || formData?.affiliationNo}
                    </Typography>
                    <Typography variant="body1" fontWeight="500">
                      {school?.schoolAddress || formData.schoolAddress}
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  LETTER OF APPOINTMENT
                </Typography>
              </Box>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Ref:</strong> EST/APP/{Math.floor(Math.random() * 10000)}/2025
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Date:</strong> {formData.issueDate}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>To,</strong>
                <br />
                {formData.teacherName}
                <br />
                {formData.fatherName && `S/o, D/o, W/o ${formData.fatherName}`}
                <br />
                {formData.address}
              </Typography>

              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Subject:</strong> Appointment Letter for the post of{' '}
                {formData.designation || '[Designation]'}.
              </Typography>

              <Typography variant="body1" sx={{ mb: 1 }}>
                Dear {formData.teacherName || 'Mr./Ms.'},
              </Typography>

              <Typography variant="body1" sx={{ mb: 1 }}>
                We are pleased to appoint you as{' '}
                <strong>{formData.designation || '[Designation]'}</strong> in the subject of{' '}
                <strong>{formData.subject || '[Subject]'}</strong> at our institution. Your
                appointment will be on a full‑time basis, starting from{' '}
                <strong>{formData.joiningDate || '[Joining Date]'}</strong>.
              </Typography>

              <Typography variant="body1" sx={{ mb: 1 }}>
                Your key responsibilities will include:
              </Typography>
              <ul>
                <li>Planning and delivering lessons as per the prescribed curriculum.</li>
                <li>Assessing and evaluating student performance.</li>
                <li>Participating in staff meetings and professional development.</li>
                <li>Maintaining discipline and a conducive learning environment.</li>
              </ul>

              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Terms of Employment:</strong>
              </Typography>
              <ul>
                <li><strong>Salary:</strong> {formData.salary || '[Salary]'}</li>
                <li><strong>Working Hours:</strong> {formData.workingHours}</li>
                <li><strong>Probation Period:</strong> {formData.probationPeriod}</li>
                <li><strong>Leave Policy:</strong> As per institution rules.</li>
                <li><strong>Termination:</strong> One month's notice from either side.</li>
              </ul>

              <Typography variant="body1" sx={{ mb: 1 }}>
                We look forward to a long and mutually beneficial association with you. Kindly
                confirm your acceptance by signing the duplicate copy of this letter.
              </Typography>

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2">Yours sincerely,</Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ mt: 4 }}>
                    {school?.principalName || formData.principalName}
                  </Typography>
                  <Typography variant="body2">(Principal)</Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2">Accepted by,</Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ mt: 4 }}>
                    {formData.teacherName || '[Teacher Name]'}
                  </Typography>
                  <Typography variant="body2">(Signature & Date)</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          {show && (
            <Stack direction="row" spacing={2} sx={{ mt: 2, justifyContent: 'flex-end' }}>
              <Button variant="outlined" component="label" startIcon={<Edit />}>
                Upload Logo
                <input type="file" accept="image/*" onChange={handleFileChange} hidden />
              </Button>
              <Button variant="outlined" startIcon={<Print />} onClick={handlePrint}>
                Print / Save as PDF
              </Button>
              {/* <Button
                variant="outlined"
                startIcon={<CloudDownload />}
                onClick={() => downloadCertificatePDF('offer-letter', 'offer-letter.pdf')}
              >
                Download PDF
              </Button> */}
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );
};